import React, { useState, useEffect } from "react";

// React Router Dom
import { Link, useNavigate, useSearchParams } from "react-router-dom";

// Hooks
import useSnackbarNotification from "../hooks/useSnackbarNotification";

// React Icons
import { FaEye, FaEyeSlash } from "react-icons/fa";

// Contexts
import { useAuth } from "../contexts/AuthContext";
import { useProfileInfo } from "../contexts/ProfileInfoContext";
import { supabase } from "../supabase";

// Utils
import { getFriendlyErrorMessage, checkAccountExists } from "../utils/errorHandling";

const LoginPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const redirectPath = searchParams.get('redirect');

    const { signIn, user } = useAuth();
    const { profileInfo, migrateFromMetadata, getProfileInfo } = useProfileInfo();
    const { showError, showSuccess } = useSnackbarNotification();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [isEmailVerificationError, setIsEmailVerificationError] = useState(false);
    const [resendingEmail, setResendingEmail] = useState(false);

    const determineCredentialErrorMessage = async (attemptedEmail) => {
        if (!attemptedEmail) return null;

        const accountInfo = await checkAccountExists(attemptedEmail, supabase);
        if (!accountInfo) return null;

        if (!accountInfo.exists) {
            return {
                message: accountInfo.message,
                field: "email",
            };
        }

        return {
            message: accountInfo.message,
            field: "password",
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setPasswordError(false);
        setEmailError(false);
        setIsEmailVerificationError(false);
        setLoading(true);

        // Client-side validation
        const trimmedEmail = email.trim();
        const trimmedPassword = password.trim();

        if (!trimmedEmail) {
            setError("Please enter your email address.");
            setEmailError(true);
            setLoading(false);
            return;
        }

        if (!trimmedPassword) {
            setError("Please enter your password.");
            setPasswordError(true);
            setLoading(false);
            return;
        }

        // Basic email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmedEmail)) {
            setError("Please enter a valid email address.");
            setEmailError(true);
            setLoading(false);
            return;
        }

        // Password length validation
        if (trimmedPassword.length < 6) {
            setError("Password must be at least 6 characters long.");
            setPasswordError(true);
            setLoading(false);
            return;
        }

        try {
            const { data: signInData, error: signInError } = await signIn(trimmedEmail, trimmedPassword);
            

            if (signInError) {
                throw signInError;
            }
            
            const signedInUser = signInData?.user;
            
            // Check if email is verified - even if login succeeded, verify email confirmation status
            if (signedInUser && !signedInUser.email_confirmed_at) {
                // Sign out the user since email is not verified
                await supabase.auth.signOut();
                throw new Error("Email not confirmed. Please verify your email address before logging in.");
            }
            
            // CRITICAL: Clear any cached profile data to ensure we get fresh data from database
            const cacheKey = `profile_${signedInUser?.id}`;
            try {
                sessionStorage.removeItem(cacheKey);
                // Clear all profile cache entries
                Object.keys(sessionStorage).forEach(key => {
                    if (key.startsWith('profile_')) {
                        sessionStorage.removeItem(key);
                    }
                });
            } catch (e) {
                // Ignore cache clearing errors
            }
            
            // Fetch profile DIRECTLY from database - ALWAYS check users_profiles table
            // This ensures we get the correct role from the database, not cache or metadata
            let { data: currentProfileInfo, error: profileError } = await supabase
                .from("users_profiles")
                .select("*")
                .eq("profileid", signedInUser?.id)
                .single();
            
            // Log the profile fetch result for debugging
            if (currentProfileInfo) {
                console.log(`[LoginPage] Profile fetched from database - Role: ${currentProfileInfo.role}, ProfileID: ${currentProfileInfo.profileid}`);
            }
            
            // Only migrate if profile truly doesn't exist (PGRST116 is "not found" error)
            // Don't migrate if profile exists but query had a different error
            if (!currentProfileInfo && profileError && profileError.code === 'PGRST116') {
                console.log('[LoginPage] No profile found in database, attempting migration from metadata');
                await migrateFromMetadata();
                // Fetch again after migration - MUST get from database
                await new Promise(resolve => setTimeout(resolve, 500));
                const { data: migratedProfile, error: migratedError } = await supabase
                    .from("users_profiles")
                    .select("*")
                    .eq("profileid", signedInUser?.id)
                    .single();
                
                if (!migratedError && migratedProfile) {
                    currentProfileInfo = migratedProfile;
                    console.log(`[LoginPage] Profile migrated - Role: ${migratedProfile.role}`);
                }
            } else if (profileError && profileError.code !== 'PGRST116') {
                // Log other errors but don't try to migrate
                console.error('[LoginPage] Error fetching profile from database:', profileError);
            }
            
            // CRITICAL: Check if profile exists with a role FROM DATABASE
            // The role MUST come from users_profiles table, not cache or metadata
            const hasProfileWithRole = currentProfileInfo && currentProfileInfo.role;
            
            if (hasProfileWithRole) {
                console.log(`[LoginPage] User has profile with role: ${currentProfileInfo.role} - Redirecting to appropriate dashboard`);
            } else {
                console.log('[LoginPage] No profile or role found - Redirecting to role selection');
            }
            
            // Force refresh profile in context to ensure it's synced with database
            if (hasProfileWithRole) {
                await getProfileInfo(true); // Force refresh from database
            }
            
            // SIMPLE LOGIC: Check if onboarding is completed
            // Database uses lowercase 'onboardingcompleted'
            const isOnboardingCompleted = currentProfileInfo?.onboardingcompleted === true;
            
            // Navigate IMMEDIATELY to prevent showing "/" route from AuthContext
            // The auth state listener will update the user context automatically
            
            // If user has a profile with a role, redirect to dashboard (even if onboarding not completed)
            // This prevents users who already selected a role from seeing role selection again
            if (hasProfileWithRole) {
                // Check if there's a redirect path from email links
                if (redirectPath) {
                    // Navigate to the redirect path (e.g., job detail page)
                    navigate(redirectPath, { replace: true });
                    showSuccess("Login successful");
                    setLoading(false);
                    return;
                }
                
                // User has a role FROM DATABASE - go to dashboard (even if onboarding not completed)
                // CRITICAL: Use exact role value from users_profiles table
                const roleFromDatabase = currentProfileInfo.role;
                console.log(`[LoginPage] Role from database: "${roleFromDatabase}"`);
                
                let dashboardPath;
                if (roleFromDatabase === 'Employer') {
                    dashboardPath = "/employer/dashboard";
                } else if (roleFromDatabase === 'Employee') {
                    dashboardPath = "/employee/dashboard";
                } else {
                    // Unknown role - redirect to role selection
                    console.warn(`[LoginPage] Unknown role "${roleFromDatabase}" - redirecting to role selection`);
                    navigate("/role-selection", { replace: true });
                    setLoading(false);
                    return;
                }
                
                // Navigate immediately with replace to override any pending navigations
                navigate(dashboardPath, { replace: true });
                showSuccess("Login successful");
                setLoading(false);
                return;
            }
            
            // No profile or no role - go to role selection
            navigate("/role-selection", { replace: true });
            setLoading(false);
        } catch (error) {
            let friendlyMessage = getFriendlyErrorMessage(error);

            // Set field-specific error states for better UX
            const normalizedMessage = error.message?.toLowerCase() || "";
            const normalizedFriendlyMessage = friendlyMessage.toLowerCase();
            
            // Check if this is an email verification error
            const isEmailVerificationError = 
                normalizedMessage.includes("email not confirmed") ||
                normalizedMessage.includes("email_not_confirmed") ||
                normalizedMessage.includes("confirmation required") ||
                normalizedMessage.includes("verify email") ||
                normalizedMessage.includes("email verification") ||
                normalizedMessage.includes("email needs to be verified") ||
                normalizedMessage.includes("email must be verified") ||
                normalizedMessage.includes("unverified email") ||
                normalizedMessage.includes("email is not verified") ||
                normalizedFriendlyMessage.includes("verify your email address");
            
            const isInvalidCredentialsError =
                normalizedMessage.includes("invalid login credentials") ||
                normalizedMessage.includes("invalid email or password") ||
                normalizedMessage.includes("invalid credentials");

            if (isEmailVerificationError) {
                // Email verification error - highlight email field
                setEmailError(true);
                setIsEmailVerificationError(true);
            } else if (isInvalidCredentialsError) {
                // Try to determine if it's email or password issue
                const credentialDetails = await determineCredentialErrorMessage(trimmedEmail);
                if (credentialDetails) {
                    friendlyMessage = credentialDetails.message;
                    if (credentialDetails.field === "email") {
                        setEmailError(true);
                    } else if (credentialDetails.field === "password") {
                        setPasswordError(true);
                        setPassword("");
                    }
                } else {
                    // Couldn't determine - highlight both fields
                    friendlyMessage = "Incorrect email or password. Please check your credentials and try again.";
                    setPasswordError(true);
                    setEmailError(true);
                    setPassword("");
                }
            } else if (normalizedMessage.includes("email")) {
                setEmailError(true);
            } else if (normalizedMessage.includes("password")) {
                setPasswordError(true);
                setPassword("");
            }

            showError(friendlyMessage);
            setError(friendlyMessage);
        }
        setLoading(false);
    };

    const handleResendVerificationEmail = async () => {
        if (!email.trim()) {
            showError("Please enter your email address first.");
            return;
        }

        setResendingEmail(true);
        try {
            // Get the current origin for the redirect URL
            const currentOrigin = window.location.origin;
            const redirectUrl = `${currentOrigin}/role-selection`;

            const resendOptions = {};
            
            // Add emailRedirectTo for https domains
            if (currentOrigin && 
                !currentOrigin.includes('localhost') && 
                !currentOrigin.includes('127.0.0.1') &&
                currentOrigin.startsWith('https://')) {
                resendOptions.emailRedirectTo = redirectUrl;
            }

            const { error: resendError } = await supabase.auth.resend({
                type: 'signup',
                email: email.trim(),
                options: resendOptions
            });

            if (resendError) throw resendError;

            showSuccess("Verification email has been resent! Please check your inbox (and spam folder).");
            setIsEmailVerificationError(false);
            setError("");
        } catch (error) {
            console.error("Resend email error:", error);
            showError("Failed to resend verification email. Please try again later.");
        } finally {
            setResendingEmail(false);
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <div className="min-h-screen flex">
            <div className="hidden md:flex md:w-1/2 bg-gray-50  flex-col space-y-6 overflow-auto">
                <img
                    src="/login.jpg"
                    alt="Login"
                    className="w-full h-full object-cover"
                />
            </div>

            <div className="w-full md:w-1/2 bg-gray-50 flex flex-col justify-center px-8 md:px-20">
                <div className="max-w-md w-full mx-auto">
                    <div
                        className="logo cursor-pointer justify-center mb-8"
                        onClick={() => navigate("/")}
                    >
                        Doctors' Jobs <span className="logo-badge">Beta</span>
                    </div>

                    <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                        Welcome Back!
                    </h2>

                    {error && (
                        <div className="mb-6 rounded-md bg-red-50 p-4 text-red-700 text-sm">
                            <div className="mb-2">{error}</div>
                            {isEmailVerificationError && (
                                <button
                                    onClick={handleResendVerificationEmail}
                                    disabled={resendingEmail || !email.trim()}
                                    className="text-sm font-medium underline hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {resendingEmail ? "Sending..." : "Resend confirmation email"}
                                </button>
                            )}
                        </div>
                    )}

                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <div>
                            <label
                                htmlFor="email"
                                className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                            >
                                Email Address *
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setEmailError(false);
                                    setError("");
                                    setIsEmailVerificationError(false);
                                }}
                                className={`appearance-none block w-full px-3 py-2 border ${
                                    emailError ? "border-red-500" : "border-gray-300"
                                } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="password"
                                className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                            >
                                Password *
                            </label>
                            <div className="relative">
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                    required
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        setPasswordError(false);
                                        setError("");
                                    }}
                                    className={`appearance-none block w-full px-3 py-2 border ${
                                        passwordError ? "border-red-500" : "border-gray-300"
                                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                />
                                <button
                                    type="button"
                                    onClick={togglePasswordVisibility}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                >
                                    {showPassword ? (
                                        <FaEyeSlash className="h-5 w-5 text-gray-400" />
                                    ) : (
                                        <FaEye className="h-5 w-5 text-gray-400" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="remember-me"
                                    name="remember-me"
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) =>
                                        setRememberMe(e.target.checked)
                                    }
                                    className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded"
                                />
                                <label
                                    htmlFor="remember-me"
                                    className="ml-2 block text-base text-gray-900"
                                >
                                    Remember me
                                </label>
                            </div>

                            <div className="text-base">
                                <a
                                    href="/forgot-password"
                                    className="font-medium text-black hover:text-black hover:underline"
                                >
                                    Forgot Password?
                                </a>
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                            >
                                {loading ? "Signing in..." : "Sign in"}
                            </button>
                        </div>
                    </form>

                    <p className="mt-6 text-center text-base text-gray-600">
                        Don't have an account?{" "}
                        <Link
                            to="/signup"
                            className="font-medium text-black hover:text-gray-700"
                        >
                            Sign Up
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
